import React from "react";
import "./CircleStatusRow.css";
import { CoinIcon } from "./RewardCard";

// Only ever link to a real web page, whatever the backend sends.
const safeJoinUrl = (url) => (/^https:\/\//i.test(String(url || "")) ? url : "");

// "Join Circle" always happens inside the menu.
const JoinButton = ({ onJoin }) => (
  <button type="button" className="circle-row-btn" onClick={onJoin}>Join Circle</button>
);

/**
 * The Circle line on the Review step for a customer who is NOT (yet) a member, one state each:
 *  - not answered yet / not eligible   nothing is shown
 *  - error         the status call failed → retry
 *  - eligible      they qualify → "Join Circle" link; once they come back the cart re-checks by itself.
 *                  With a verified email on the account it is just the button; otherwise the full card.
 * Members keep the coins + Rewards row, rendered by CartPage.
 */
export default function CircleStatusRow({ circle, loading, error, onRetry, onRefresh, emailVerified, firstName, onJoin }) {
  if (error) {
    return (
      <div className="circle-row circle-row-error" role="status">
        <p className="circle-row-title">Couldn't check your Circle status</p>
        <button type="button" className="circle-row-link" onClick={onRetry}>Try again</button>
      </div>
    );
  }

  // Not answered yet: show nothing. Most customers have no Circle line at all, so a "checking" row that then
  // disappears would only flash; members / eligible customers get their row as soon as the answer is in.
  if (!circle) return null;

  const joinUrl = circle.canJoin ? safeJoinUrl(circle.joinUrl) : "";

  if (joinUrl) {
    // They have just come back from joining (or are re-checking): say so instead of showing a stale button.
    if (loading) {
      return (
        <div className="circle-row circle-row-checking" role="status" aria-busy="true">
          <span className="circle-row-spinner" aria-hidden="true" />
          <p className="circle-row-title">Checking your membership…</p>
        </div>
      );
    }
    if (emailVerified === true) {
      return (
        <div className="circle-row circle-row-join circle-row-join-compact">
          <CoinIcon size={30} />
          <p className="circle-row-title">{firstName ? `${firstName}, you're eligible for Circle` : "You're eligible for Circle"}</p>
          <JoinButton onJoin={onJoin} />
        </div>
      );
    }
    return (
      <div className="circle-row circle-row-join">
        <div className="circle-row-head">
          <CoinIcon size={30} />
          <div>
            <p className="circle-row-title">You're eligible for Circle</p>
            <p className="circle-row-sub">Join to earn Circle Coins on your orders and unlock rewards.</p>
          </div>
        </div>
        <div className="circle-row-actions">
          <JoinButton onJoin={onJoin} />
          <button type="button" className="circle-row-link" onClick={onRefresh}>Already joined? Refresh</button>
        </div>
      </div>
    );
  }

  // Not a member and can't join yet: nothing to show on the cart. (How far they are is on the Rewards page.)
  return null;
}
